import { Link } from 'react-router-dom';
import PolicyLayout, {
  PolicyContact,
  PolicyList,
  PolicySection,
  PolicySubsection,
} from '../components/PolicyLayout';
import { PHONE, SITE_DOMAIN, SITE_NAME } from '../constants/site';

const UPDATED = 'May 25, 2026';

export default function PriceMatchPromisePage() {
  return (
    <PolicyLayout title="Price Match Promise" lastUpdated={UPDATED}>
      <PolicySection title="1. Our commitment">
        <p>
          {SITE_NAME} strives to offer competitive pricing on flights, hotels, cars, packages, and cruises. Our Price
          Match Promise allows eligible customers to request a review when they find the same itinerary at a lower
          total price from a qualifying U.S.-based competitor after booking through {SITE_DOMAIN}.
        </p>
      </PolicySection>

      <PolicySection title="2. Eligibility requirements">
        <PolicyList
          items={[
            'You must have a confirmed, paid booking through Farenodex with a valid confirmation number.',
            'The competing offer must be for the identical itinerary: same airline/flight numbers, dates, times, cabin, and passenger count.',
            'The competing offer must be publicly available in USD from a legitimate U.S. online travel agency or airline website (not a member-only, coupon-only, or error fare).',
            'The competing total must include all mandatory taxes and fees disclosed at checkout on both sites.',
            'Requests must be submitted within twenty-four (24) hours of your Farenodex booking confirmation.',
          ]}
        />
      </PolicySection>

      <PolicySection title="3. Exclusions">
        <PolicyList
          items={[
            'Different baggage allowances, fare brands, or refundability terms.',
            'Corporate, group, package-only, or opaque inventory rates.',
            'Prices from auction sites, peer-to-peer marketplaces, or unverified sellers.',
            'Bank/card promotions, cashback portals, or loyalty redemptions not reflected in the competitor checkout total.',
            'Post-ticketing changes, reissues, or bookings already modified from the original purchase.',
          ]}
        />
      </PolicySection>

      <PolicySection title="4. How to submit a price match request">
        <PolicySubsection title="4.1 Required information">
          <p>Email or call us with:</p>
          <PolicyList
            items={[
              'Your Farenodex confirmation number and passenger names',
              'URL or screenshot of the competitor offer showing the lower total and identical itinerary',
              'Date and time you found the competing price',
              'Contact phone and email on the booking',
            ]}
          />
        </PolicySubsection>
        <PolicySubsection title="4.2 Verification">
          <p>
            Our team verifies that the competing offer is bookable at the stated price and matches your booking. We may
            attempt to replicate the fare in our systems. Verification typically completes within one (1) to three (3)
            business days.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="5. Remedies">
        <p>
          If your request is approved, we may, at our discretion: (a) refund the difference between your paid total and
          the verified competitor total, (b) issue a travel credit for the difference, or (c) cancel and rebook at the
          lower fare if feasible without additional airline penalties. Maximum benefit per booking is capped at USD $200
          per passenger unless otherwise stated in a promotion.
        </p>
      </PolicySection>

      <PolicySection title="6. Limitations">
        <p>
          The Price Match Promise is a goodwill program, not a guarantee of lowest price on every search. We reserve the
          right to decline requests that cannot be verified, involve fare errors, or conflict with supplier rules.
          Program terms may change without notice; the version in effect at your booking date applies.
        </p>
      </PolicySection>

      <PolicySection title="7. Contact us">
        <p>
          Submit requests via <Link to="/flight/support">Contact Us</Link> or call{' '}
          <a href={`tel:${PHONE.replace(/[^\d+]/g, '')}`}>{PHONE}</a>.
        </p>
        <PolicyContact />
      </PolicySection>
    </PolicyLayout>
  );
}
